import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import SuccessToast from '../../../shared/components/ui/SuccessToast';
import { useScrollToTop } from '../../../utils/scrollUtils';
import ChangePasswordModal from '../components/ChangePasswordModal';
import ProfileDetails from '../components/ProfileDetails';
import ProfileEditForm from '../components/ProfileEditForm';
import ProfileHero from '../components/ProfileHero';
import ProfileSkeleton from '../components/ProfileSkeleton';
import ProfileTabs, { PROFILE_TABS } from '../components/ProfileTabs';
import TeamStructure from '../../admin/components/TeamStructure/TeamStructure';
import { useAvatarUpload } from '../hooks/useAvatarUpload';
import { useProfile } from '../hooks/useProfile';
import { getUserId } from '../utils/profileFormat';
import './Profile.css';

const TOAST_DURATION_MS = 4000;

/**
 * "My Profile" page: the logged-in user's details from GET /rfp/auth/users/{user_id}, which they
 * can edit (PATCH), a picture they can change, and a Change password dialog.
 */
const Profile = ({ user, onUserUpdate }) => {
    useScrollToTop([]);

    const userId = getUserId(user);
    const { profile, status, error, reload, applyProfile } = useProfile(userId);

    const [isEditing, setIsEditing] = useState(false);
    const [activeTab, setActiveTab] = useState(PROFILE_TABS.PROFILE);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [toast, setToast] = useState(null);

    // The server answers an upload with the full updated profile, including the new picture.
    const handleAvatarSaved = useCallback((updatedProfile) => {
        applyProfile(updatedProfile);
        // The top bar and side bar show the new picture too.
        if (onUserUpdate) onUserUpdate({ avatar_url: updatedProfile?.avatar_url || '' });
        setToast({ message: 'Your profile photo was updated.', type: 'success' });
    }, [applyProfile, onUserUpdate]);
    const handleAvatarRemoved = useCallback((updatedProfile) => {
        applyProfile(updatedProfile);
        if (onUserUpdate) onUserUpdate({ avatar_url: updatedProfile?.avatar_url || '' });
        setToast({ message: 'Your profile photo was removed.', type: 'success' });
    }, [applyProfile, onUserUpdate]);
    const avatarUpload = useAvatarUpload(userId, handleAvatarSaved, handleAvatarRemoved);

    const startEditing = useCallback(() => {
        setActiveTab(PROFILE_TABS.PROFILE);
        setIsEditing(true);
    }, []);
    const stopEditing = useCallback(() => setIsEditing(false), []);
    const closePasswordModal = useCallback(() => setShowPasswordModal(false), []);
    const handlePasswordChanged = useCallback((message) => setToast({ message, type: 'success' }), []);

    // The server answers a save with the full updated profile: show it and leave edit mode.
    const handleSaved = useCallback((updatedProfile) => {
        applyProfile(updatedProfile);
        setIsEditing(false);
        setToast({ message: 'Your profile was updated successfully.', type: 'success' });
    }, [applyProfile]);

    useEffect(() => {
        if (!toast) return undefined;
        const timer = setTimeout(() => setToast(null), TOAST_DURATION_MS);
        return () => clearTimeout(timer);
    }, [toast]);

    return (
        <div className="prof-page">
            <SuccessToast toast={toast} />

            <div className="prof-page-heading">
                <h1>My Profile</h1>
                <p>View your details, keep them up to date and manage your account.</p>
            </div>

            {status === 'loading' && !profile && <ProfileSkeleton />}

            {status === 'error' && (
                <div className="prof-card prof-state prof-state-error" role="alert">
                    <span>{error}</span>
                    {userId && (
                        <button type="button" className="prof-btn prof-btn-ghost prof-btn-small" onClick={reload}>
                            <RefreshCw size={14} aria-hidden="true" />
                            Try again
                        </button>
                    )}
                </div>
            )}

            {profile && (
                <>
                    <ProfileHero
                        profile={profile}
                        upload={avatarUpload}
                        isEditing={isEditing}
                        onEdit={startEditing}
                        onChangePassword={() => setShowPasswordModal(true)}
                    />

                    <ProfileTabs
                        active={activeTab}
                        onChange={setActiveTab}
                        teamDisabled={isEditing}
                        teamHint="Save or cancel your changes first"
                    />

                    <div id="prof-panel" role="tabpanel" className="prof-panel">
                        {activeTab === PROFILE_TABS.TEAM ? (
                            <section className="prof-card prof-team-card">
                                <header className="prof-team-head">
                                    <h2>Team Structure</h2>
                                    <p>View the reporting hierarchy and team members.</p>
                                </header>
                                <TeamStructure
                                    key={userId}
                                    user={{
                                        name: profile.fullName,
                                        rawRole: profile.role,
                                        avatar_url: profile.avatarUrl,
                                        status: profile.statusKey,
                                    }}
                                    userId={userId}
                                    canOpenProfiles={profile.role.toLowerCase().includes('admin')}
                                />
                            </section>
                        ) : isEditing ? (
                            <ProfileEditForm
                                profile={profile}
                                userId={userId}
                                onSaved={handleSaved}
                                onCancel={stopEditing}
                            />
                        ) : (
                            <ProfileDetails profile={profile} />
                        )}
                    </div>
                </>
            )}

            {showPasswordModal && (
                <ChangePasswordModal onClose={closePasswordModal} onSuccess={handlePasswordChanged} />
            )}
        </div>
    );
};

export default Profile;
